"use client";

import React from "react";
import { MapPin, Phone, Mail, Globe, Loader, Map } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const contactDetails = [{
  icon: Phone,
  label: "Phone",
  value: "(970) 279-3344",
  link: "tel:(970) 279-3344"
}, {
  icon: Globe,
  label: "Website",
  value: "durangodogmassage.com",
  link: "/HomePage"
}, {
  icon: Mail,
  label: "Email",
  value: "durangodogmassage@gmail.com",
  link: "mailto:durangodogmassage@gmail.com"
}];
function AccessibilityForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="224152" className="flex flex-col items-center justify-center py-12 text-center space-y-4">
        <div className="size-16 bg-accent text-accent-foreground rounded-lg flex items-center justify-center mb-4">
          <svg className="size-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-2xl font-serif font-semibold text-foreground">Thank You!</h3>
        <p className="text-muted-foreground text-lg max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-2 h-12 px-8 text-lg rounded-lg">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Your name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium text-foreground font-default uppercase tracking-wider">Your Name *</FormLabel>
              <FormControl>
                <Input placeholder="Enter your name" className="border border-input/50 h-11 text-base bg-muted/20 rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium text-foreground font-default uppercase tracking-wider">Email Address *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jessica@example.com" className="border border-input/50 h-11 text-base bg-muted/20 rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="organization" rules={{
        required: "Dog's name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium text-foreground font-default uppercase tracking-wider">Dog's Name *</FormLabel>
              <FormControl>
                <Input placeholder="Buddy" className="border border-input/50 h-11 text-base bg-muted/20 rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="role" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium text-foreground font-default uppercase tracking-wider">Breed</FormLabel>
              <FormControl>
                <Input placeholder="Golden Retriever" className="border border-input/50 h-11 text-base bg-muted/20 rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium text-foreground font-default uppercase tracking-wider">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (970) 555-0123" className="border border-input/50 h-11 text-base bg-muted/20 rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="service_interest" rules={{
        required: "Please select a primary concern"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium text-foreground font-default uppercase tracking-wider">Primary Concern *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="border border-input/50 h-11 text-base bg-muted/20 rounded-lg w-full">
                    <SelectValue placeholder="Select a concern" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Mobility Support">Mobility Support</SelectItem>
                  <SelectItem value="Post-Hike Recovery">Post-Hike Recovery</SelectItem>
                  <SelectItem value="Senior Dog Comfort">Senior Dog Comfort</SelectItem>
                  <SelectItem value="Relaxation & Stress Relief">Relaxation & Stress Relief</SelectItem>
                  <SelectItem value="Performance Support">Performance Support</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription className="text-sm text-muted-foreground">
                Choose the focus area for our initial bodywork session.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} variant="destructive" className="w-full h-14 text-lg font-semibold mt-4 shadow-md hover:shadow-lg transition-all rounded-lg border-2 border-transparent hover:bg-transparent hover:text-destructive hover:border-destructive">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-3 size-6" />
            Sending Inquiry...
          </> : 'Submit Inquiry'}
      </Button>
    </Form>;
}
export default function StartYourAccessibilityJourney() {
  return <section id="lets-start-the-healing" className="bg-background py-16 md:py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          <div className="lg:col-span-5 flex flex-col space-y-10">
            <div>
              <h2 className="text-4xl font-serif font-bold text-foreground tracking-tight mb-4">
                Let's Start the Healing
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Whether your companion is a trail-tested athlete recovering from a weekend in the San Juans or a senior dog needing extra comfort, Jessica Gade, DPT, CSMT, provides professional bodywork tailored to their needs. Reach out to schedule a session in your home or at our shared clinical space.
              </p>
            </div>

            <div className="space-y-6">
              {contactDetails.map((detail, i) => <div key={i} data-index={i} className="flex items-start space-x-4">
                  <div className="bg-accent p-3 rounded-lg text-primary shrink-0">
                    <detail.icon className="size-6" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-1 font-default">
                      {detail.label}
                    </h3>
                    <Link to={detail.link} className="text-lg font-medium text-foreground hover:text-secondary transition-colors" newTab={detail.label === "Website"}>
                      {detail.value}
                    </Link>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 rounded-lg overflow-hidden border border-border bg-muted/30 aspect-video flex flex-col items-center justify-center relative group">
              <div className="absolute inset-0 bg-cover bg-center opacity-20 grayscale group-hover:grayscale-0 group-hover:opacity-30 transition-all duration-500 bg-[url(/img/durango_mountain_map_bg.png)]"></div>
              <Map className="size-12 text-muted-foreground mb-3 relative z-10" />
              <p className="text-muted-foreground font-medium relative z-10 font-default">Durango Dog Massage</p>
              <p className="text-sm text-muted-foreground/80 relative z-10">Durango, Colorado & Surrounding Areas</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Card className="border-none border-l-4 border-secondary shadow-xl shadow-shadow-color/5 bg-card overflow-hidden rounded-lg">
              <CardHeader className="bg-muted/10 pb-8 pt-8 px-8">
                <CardTitle className="text-3xl font-serif text-foreground">
                  Let's Start the Healing - Contact Form
                </CardTitle>
                <CardDescription className="text-base text-muted-foreground mt-2">
                  Fill out the form below to share your dog's history. We will review the details and reach out within 24 hours to schedule a consultation.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-8">
                <FormProvider formKey="start_healing_form_contact" formId="start_healing_form_contact" sectionName="Let's Start the Healing" defaultValues={{
                full_name: "",
                email: "",
                organization: "",
                role: "",
                phone: "",
                service_interest: ""
              }} submitText="Submit Inquiry" successMessage="Thank you for reaching out! We will review your dog's information and contact you shortly to discuss their healing journey.">
                  <AccessibilityForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}